import {useIntl} from 'react-intl'
import * as Yup from 'yup'
import {regexDomain} from 'src/feature/bite/helpers/webSiteRegex'

export default function useValidationSchemaRenderer() {
  const intl = useIntl()

  const validationSchemaFinancial = Yup.object().shape({
    financialOfficialName: Yup.string().required(
      intl.formatMessage({id: 'GLOBAL.VALIDATION.REQUIRED_FIELD'})
    ),
    financialInvoiceAddress: Yup.string().required(
      intl.formatMessage({id: 'GLOBAL.VALIDATION.REQUIRED_FIELD'})
    ),
    financialBankName: Yup.string().required(
      intl.formatMessage({id: 'GLOBAL.VALIDATION.REQUIRED_FIELD'})
    ),
    financialTaxOffice: Yup.string().required(
      intl.formatMessage({id: 'GLOBAL.VALIDATION.REQUIRED_FIELD'})
    ),
    financialIBAN: Yup.string().required(
      intl.formatMessage({id: 'GLOBAL.VALIDATION.REQUIRED_FIELD'})
    ),
    financialTaxNumber: Yup.string().required(
      intl.formatMessage({id: 'GLOBAL.VALIDATION.REQUIRED_FIELD'})
    ),
  })

  const validationSchemaContract = Yup.object().shape({})

  const validationSchemaSocialMedia = Yup.object().shape({
    facebook: Yup.string(),
    linkedIn: Yup.string(),
    instagram: Yup.string(),
    twitter: Yup.string(),
  })

  const validationSchemaFacility = Yup.object().shape({
    infoContentData: Yup.array()
      .of(
        Yup.object().shape({
          data: Yup.string()
            .optional()
            .nullable()
            .test(
              'additionalInformations',
              intl.formatMessage({id: 'GLOBAL.VALIDATION.MAX_LENGTH_FIELD'}, {length: 1000}),
              (value) => {
                let htmlEl = document.createElement('div')
                if (value && htmlEl) {
                  htmlEl.innerHTML = value
                  const textValue = htmlEl.innerText.trim()

                  if (textValue.length > 1000) {
                    return false
                  }
                }

                return true
              }
            ),
          id: Yup.string().optional().nullable(),
          name: Yup.string().optional().nullable(),
          contentURL: Yup.string().optional().nullable(),
        })
      )
      .test('aboutUs', intl.formatMessage({id: 'GLOBAL.VALIDATION.REQUIRED_FIELD'}), (value) => {
        const aboutUsValue = value?.find(
          (section: any) => section.name === 'Additional Information'
        )

        if (!aboutUsValue) {
          return false
        }

        let htmlEl = document.createElement('div')
        if (htmlEl) {
          htmlEl.innerHTML = aboutUsValue?.data || ''
          const textValue = htmlEl.innerText.trim()

          if (textValue.length === 0) {
            return false
          }
        }

        return true
      }),
    additionalInformations: Yup.string()
      .required(intl.formatMessage({id: 'GLOBAL.VALIDATION.REQUIRED_FIELD'}))
      .test(
        'additionalInformations',
        intl.formatMessage({id: 'GLOBAL.VALIDATION.REQUIRED_FIELD'}),
        (value) => {
          let htmlEl = document.createElement('div')
          if (value && htmlEl) {
            htmlEl.innerHTML = value
            const textValue = htmlEl.innerText.trim()

            if (textValue.length === 0) {
              return false
            }
          }

          return true
        }
      )
      .test(
        'additionalInformations',
        intl.formatMessage({id: 'GLOBAL.VALIDATION.MAX_LENGTH_FIELD'}, {length: 1000}),
        (value) => {
          let htmlEl = document.createElement('div')
          if (value && htmlEl) {
            htmlEl.innerHTML = value
            const textValue = htmlEl.innerText.trim()

            if (textValue.length > 1000) {
              return false
            }
          }

          return true
        }
      ),
    imagesData: Yup.array()
      .of(
        Yup.object().shape({
          imageUrl: Yup.string().required(
            intl.formatMessage({id: 'GLOBAL.VALIDATION.REQUIRED_FIELD'})
          ),
        })
      )
      .required(intl.formatMessage({id: 'GLOBAL.VALIDATION.REQUIRED_FIELD'}))
      .test('image', intl.formatMessage({id: 'GLOBAL.VALIDATION.REQUIRED_FIELD'}), (value) => {
        if (value?.length === 0) {
          return false
        }

        return true
      }),
  })

  const validationSchemaContact = Yup.object().shape({
    contactName: Yup.string().required(
      intl.formatMessage({id: 'GLOBAL.VALIDATION.REQUIRED_FIELD'})
    ),
    contactSurname: Yup.string().required(
      intl.formatMessage({id: 'GLOBAL.VALIDATION.REQUIRED_FIELD'})
    ),
    contactPhone: Yup.string()
      .test({
        name: 'contactPhone',
        message: intl.formatMessage({id: 'GLOBAL.VALIDATION.PHONE'}),
        test: (value) => {
          if (value) {
            const regex = /^\+?[0-9]{1,4}?\s?[0-9]{2,3}\s?[0-9]{3}\s?[0-9]{4}$/
            return regex.test(value)
          }
          return true
        },
      })
      .required(intl.formatMessage({id: 'GLOBAL.VALIDATION.REQUIRED_FIELD'})),
    contactEmail: Yup.string()
      .email(intl.formatMessage({id: 'GLOBAL.VALIDATION.EMAIL'}))
      .required(intl.formatMessage({id: 'GLOBAL.VALIDATION.REQUIRED_FIELD'})),
    contactTitle: Yup.string(),
  })

  const validationSchemaOverview = Yup.object().shape({
    status: Yup.number().required(intl.formatMessage({id: 'GLOBAL.VALIDATION.REQUIRED_FIELD'})),
    officialName: Yup.string().required(
      intl.formatMessage({id: 'GLOBAL.VALIDATION.REQUIRED_FIELD'})
    ),
    categoryIDs: Yup.array()
      .of(Yup.string())
      .min(1, intl.formatMessage({id: 'GLOBAL.VALIDATION.REQUIRED_FIELD'}))
      .required(intl.formatMessage({id: 'GLOBAL.VALIDATION.REQUIRED_FIELD'})),
    subCategoryIDs: Yup.array()
      .of(Yup.string())
      .min(1, intl.formatMessage({id: 'GLOBAL.VALIDATION.REQUIRED_FIELD'}))
      .required(intl.formatMessage({id: 'GLOBAL.VALIDATION.REQUIRED_FIELD'})),
    slipName: Yup.string().max(
      20,
      intl.formatMessage({id: 'GLOBAL.VALIDATION.MAX_LENGTH_FIELD'}, {length: 20})
    ),
    slipSlogan: Yup.string().max(
      50,
      intl.formatMessage({id: 'GLOBAL.VALIDATION.MAX_LENGTH_FIELD'}, {length: 50})
    ),
    mccCode: Yup.string().max(
      50,
      intl.formatMessage({id: 'GLOBAL.VALIDATION.MAX_LENGTH_FIELD'}, {length: 50})
    ),
    webSite: Yup.string()
      .matches(regexDomain, intl.formatMessage({id: 'GLOBAL.VALIDATION.WEBSITE'}))
      .required(intl.formatMessage({id: 'GLOBAL.VALIDATION.REQUIRED_FIELD'})),
  })

  const renderer = (editable: {
    overview: boolean
    contact: boolean
    facility: boolean
    socialMedia: boolean
    contract: boolean
    financial: boolean
  }) => {
    if (editable.overview) {
      return validationSchemaOverview
    } else if (editable.contact) {
      return validationSchemaContact
    } else if (editable.facility) {
      return validationSchemaFacility
    } else if (editable.socialMedia) {
      return validationSchemaSocialMedia
    } else if (editable.contract) {
      return validationSchemaContract
    } else if (editable.financial) {
      return validationSchemaFinancial
    }
    return Yup.object().shape({})
  }

  return {renderer}
}
